// Аналитика в приложении. Правила — apps/api/src/analytics.ts (каталог ниже должен с ним совпадать, это проверяет тест).
//  • экраны и нажатия кнопок пишутся сами (screen_viewed, button_clicked);
//  • остальное — track('имя', {поля}) только из каталога, TypeScript не даст написать чужое имя или поле;
//  • никаких имён, телефонов, адресов и текстов: вместо текста — факт и длина.
// Кнопку, текст которой может содержать личное (имя человека), помечаем data-noev — её нажатие пишется без подписи.

import { initData, inTelegram, startParam } from './tg.ts';
import { hasSession, localSession, onSite } from './web.ts';

export const CLIENT_EVENTS = {
  app_opened: ['start', 'returning'],
  consent_answered: ['pd', 'ads'],
  screen_viewed: ['from'],
  button_clicked: ['label'],
  flow_started: ['mode'],
  gift_recipient_chosen: ['relation', 'known_person'],
  gift_occasion_chosen: ['occasion'],
  gift_method_chosen: ['method'],
  slot_chosen: ['day', 'slot', 'busy'],
  slot_by_recipient: ['day', 'from'],
  size_chosen: ['size', 'suggested'],
  swipe_answered: ['step', 'card', 'answer', 'via'],
  flowers_answered: ['likes', 'nopes'],
  portrait_answered: ['character', 'colors', 'nope', 'has_note', 'note_len'],
  picks_shown: ['designs', 'again', 'mode'],
  design_opened: ['design', 'position'],
  design_chosen: ['design', 'position', 'size'],
  surprise_chosen: ['size'],
  checkout_started: ['kind', 'total', 'zone'],
  checkout_error: ['message'],
  promo_applied: ['kind', 'discount'],
  date_saved: ['occasion', 'yearly', 'remind'],
  taste_saved: ['for_person', 'changes'],
  support_sent: ['topic', 'with_photo', 'len'],
  error_shown: ['message'],
  site_visited: ['utm'],
  site_to_telegram: ['button'],
  site_start: ['button'],
  site_my_orders: ['sent'],
} as const;

type Catalog = typeof CLIENT_EVENTS;
export type EventName = keyof Catalog;
type Value = string | number | boolean | null | Array<string | number>;
type PropsOf<K extends EventName> = Partial<Record<Catalog[K][number], Value>>;

const BASE = (import.meta as any).env?.VITE_API_URL ?? '';
const VERSION = (import.meta as any).env?.VITE_APP_VERSION ?? 'dev';

function newSession() {
  return Math.random().toString(36).slice(2, 10);
}
let session = newSession();
try {
  session = sessionStorage.getItem('vyd.session') ?? session;
  sessionStorage.setItem('vyd.session', session);
} catch {
  /* приватный режим */
}

/** Метка источника: ?r=blog_anya на сайте или startapp=r_blog_anya в Telegram. */
function refTag(): string | undefined {
  const sp = startParam();
  if (sp?.startsWith('r_')) return sp.slice(2);
  const q = new URLSearchParams(location.search);
  return q.get('r') ?? q.get('utm_source') ?? undefined;
}

let queue: Array<{ name: string; screen?: string; props?: Record<string, unknown>; at: number; orderId?: string }> = [];
let currentScreen: string | undefined;
let timer: ReturnType<typeof setTimeout> | null = null;

function flush() {
  timer = null;
  if (!queue.length) return;
  const events = queue.splice(0, 50);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (inTelegram) headers.Authorization = `tma ${initData()}`;
  // На сайте — сессия, если она уже есть (сами её ради событий не заводим).
  else if (onSite && hasSession()) headers.Authorization = `web ${localSession()}`;
  else if (location.hostname === 'localhost') headers['X-Dev-User'] = '1';
  // keepalive — чтобы события долетели, даже если приложение закрывают.
  fetch(BASE + '/api/events', { method: 'POST', headers, keepalive: true, body: JSON.stringify({ session, v: VERSION, ref: refTag(), events }) }).catch(() => {});
  if (queue.length) schedule();
}

function schedule() {
  if (!timer) timer = setTimeout(flush, 1000);
}

export function track<K extends EventName>(name: K, props: PropsOf<K> = {}, opts: { orderId?: string } = {}) {
  queue.push({ name, screen: currentScreen, props: props as Record<string, unknown>, at: Date.now(), orderId: opts.orderId });
  schedule();
}

/** Сменился экран (вызывает store). */
export function trackScreen(name: string) {
  const from = currentScreen;
  currentScreen = name;
  track('screen_viewed', { from: from ?? null });
}

/** Все нажатия кнопок пишутся сами: подпись — data-ev, иначе текст кнопки (без цифр и не длиннее 40 знаков). */
export function autoTrackClicks() {
  document.addEventListener(
    'click',
    (e) => {
      const el = (e.target as HTMLElement | null)?.closest?.('button, a, [role="button"]') as HTMLElement | null;
      if (!el) return;
      const priv = el.closest('[data-noev]');
      const label = el.dataset.ev ?? (priv ? 'private' : (el.getAttribute('aria-label') ?? (el.innerText || el.textContent || '').split('\n').map((x) => x.trim()).find(Boolean) ?? '').replace(/\d[\d\s ₽.,:–-]*/g, '#').replace(/\s+/g, ' ').trim().slice(0, 40));
      if (label) track('button_clicked', { label });
    },
    { capture: true },
  );
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

export function trackAppOpened() {
  let returning = false;
  try {
    returning = localStorage.getItem('vyd.seen') === '1';
    localStorage.setItem('vyd.seen', '1');
  } catch {
    /* ничего */
  }
  const sp = startParam();
  track('app_opened', { start: sp ? sp.split(/[_:]/)[0] : null, returning });
}
